import type { ApolloClient } from "@apollo/client";
import {
  type IssueRow,
  type IssueViewLiveRow,
  IssueViewLiveFragmentDoc,
  IssueViewMovedDocument,
  IssueViewUpdatedDocument,
} from "#app/lib/data";
import {
  defineSubscription,
  type AnySubscriptionRequest,
} from "#app/lib/subscriptions/subscription-provider";
import { recordAutomationTouch } from "#app/lib/automation-touches";
import {
  identifyIssueInCache,
  writeIssueToCacheViaClient,
} from "#app/hooks/kanban/kanban-cache-utils";

// ---------------------------------------------------------------------------
// Issue-view realtime — registered by the issue view for the open issue's
// workflow, using the dialog-scoped IssueViewUpdated/IssueViewMoved documents
// (issue-view.graphql).
//
// Both events are handled because the server publishes ISSUE_MOVED xor
// ISSUE_UPDATED — a save that edits fields AND changes stage publishes only
// the former, and the open dialog must not go stale on it. Column membership
// on a mounted board stays the board's own IssueMoved handler's job
// (kanban-subscriptions.ts); this handler writes the entity only.
// ---------------------------------------------------------------------------

type IssueViewPayload = IssueRow & IssueViewLiveRow;

function writeIssueViewPayload(client: ApolloClient, issue: IssueViewPayload): void {
  writeIssueToCacheViaClient(client, issue);
  const cacheId = identifyIssueInCache(client.cache, issue.issueId);
  if (!cacheId) return;
  client.cache.writeFragment({
    id: cacheId,
    fragment: IssueViewLiveFragmentDoc,
    fragmentName: "IssueViewLive",
    data: issue,
  });
}

export function issueViewSubscriptions(
  workflowId: string,
): AnySubscriptionRequest[] {
  const variables = { workflowId };
  return [
    defineSubscription({
      document: IssueViewUpdatedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueUpdated;
        if (!event) return;
        recordAutomationTouch(event, "updated");
        writeIssueViewPayload(client, event.issue);
      },
    }),
    defineSubscription({
      document: IssueViewMovedDocument,
      variables,
      onData: (client, data) => {
        const event = data.issueMoved;
        if (!event) return;
        recordAutomationTouch(event, "moved");
        writeIssueViewPayload(client, event.issue);
      },
    }),
  ];
}
